import { createRouter, createWebHistory } from 'vue-router'
import AdminLayout from '@/layouts/AdminLayout.vue'
import { useAuthStore } from '@/stores/auth'
import DashboardView from '@/views/DashboardView.vue'
import LoginView from '@/views/LoginView.vue'

/**
 * Every module route carries the permission it needs. The guard uses it to decide what to show;
 * the server checks again on every call, so this is convenience, not security — it stops an
 * operator opening a screen that would answer 403 on its first request.
 */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/login', name: 'login', component: LoginView, meta: { public: true } },

    /*
     * The warehouse lane (G6.13). Its own shell rather than `AdminLayout`: a picker holding a box
     * needs one screen and a way back, not a sidebar of forty destinations.
     *
     * A sibling of `/` rather than a child, so it inherits the global guard — same session, same
     * `meta.permission` check — while inheriting none of the desktop chrome. Lazy, so the desktop
     * panel never downloads a screen it will not draw.
     *
     * `shipping.*` is gated inside the screens rather than on the route: an operator who may read
     * orders but not dispatch should still get a working picking list.
     */
    {
      path: '/m',
      component: () => import('@/layouts/MobileLayout.vue'),
      children: [
        { path: '', redirect: '/m/orders' },
        {
          path: 'orders',
          name: 'm-orders',
          component: () => import('@/views/mobile/MobileOrderQueueView.vue'),
          meta: { permission: 'orders.read', mobileTitle: 'صف امروز' },
        },
        {
          path: 'orders/:id',
          name: 'm-order',
          component: () => import('@/views/mobile/MobileOrderView.vue'),
          meta: { permission: 'orders.read', mobileTitle: 'سفارش' },
        },
        {
          path: 'support',
          name: 'm-support',
          component: () => import('@/views/mobile/MobileSupportView.vue'),
          meta: { permission: 'support.read', mobileTitle: 'پشتیبانی' },
        },
        {
          path: 'support/:id',
          name: 'm-support-thread',
          component: () => import('@/views/mobile/MobileSupportView.vue'),
          meta: { permission: 'support.read', mobileTitle: 'گفت‌وگو' },
        },
      ],
    },
    {
      path: '/',
      component: AdminLayout,
      children: [
        { path: '', redirect: '/dashboard' },
        { path: 'dashboard', name: 'dashboard', component: DashboardView, meta: { permission: null } },

        {
          path: 'orders',
          name: 'orders',
          component: () => import('@/views/orders/OrderListView.vue'),
          meta: { permission: 'orders.read' },
        },
        {
          path: 'orders/:id',
          name: 'order',
          component: () => import('@/views/orders/OrderDetailView.vue'),
          meta: { permission: 'orders.read' },
        },
        /*
         * برگهٔ بسته‌بندی (G6.8). Under the order rather than under a section of its own, because it
         * is one order's document and there is no list of packing slips to land on.
         *
         * `orders.read` and nothing more: the slip only reads, and the bench that prints it is not
         * the desk that moves an order's status.
         */
        {
          path: 'orders/:id/packing-slip',
          name: 'order-packing-slip',
          component: () => import('@/views/orders/PackingSlipView.vue'),
          meta: { permission: 'orders.read' },
        },

        {
          path: 'journeys',
          name: 'journeys',
          component: () => import('@/views/journeys/JourneyAnalyticsView.vue'),
          meta: { permission: 'journeys.analytics.read' },
        },
        {
          path: 'journeys/editor',
          name: 'journey-editor',
          component: () => import('@/views/journeys/JourneyEditorView.vue'),
          meta: { permission: 'journeys.definitions.read' },
        },

        /*
         * گزارش‌ها (G1.3.6). Seven screens, one component; the route only decides which key and
         * which permission.
         *
         * `meta.permission` carries a single string, and the guard above reads it as one. Where a
         * screen needs a second permission — settlements needs `payments.read` as well as
         * `reports.view` — the route declares the one that decides whether the screen exists at
         * all, and the screen itself requires the full set before it will call the API. Both are
         * conveniences either way: the server checks every `/admin/v1/reports` call again.
         */
        {
          path: 'reports/accounting',
          name: 'report-accounting',
          component: () => import('@/views/reports/AccountingReportView.vue'),
          meta: { permission: 'reports.view' },
        },
        {
          path: 'reports/cart-recovery',
          name: 'report-cart-recovery',
          component: () => import('@/views/reports/CartRecoveryReportView.vue'),
          meta: { permission: 'reports.view' },
        },
        {
          path: 'reports/settlements',
          name: 'report-settlements',
          component: () => import('@/views/reports/SettlementsReportView.vue'),
          meta: { permission: 'reports.view' },
        },
        {
          path: 'reports/vat',
          name: 'report-vat',
          component: () => import('@/views/reports/VatReportView.vue'),
          meta: { permission: 'reports.finance' },
        },
        {
          path: 'reports/margin',
          name: 'report-margin',
          component: () => import('@/views/reports/MarginReportView.vue'),
          meta: { permission: 'reports.finance' },
        },
        {
          path: 'reports/journeys-revenue',
          name: 'report-journeys-revenue',
          component: () => import('@/views/reports/JourneyRevenueReportView.vue'),
          meta: { permission: 'reports.view' },
        },
        {
          path: 'reports/views',
          name: 'report-views',
          component: () => import('@/views/reports/ProductViewsReportView.vue'),
          meta: { permission: 'reports.view' },
        },
        {
          path: 'reports/search',
          name: 'report-search',
          component: () => import('@/views/reports/SearchAnalyticsReportView.vue'),
          meta: { permission: 'reports.view' },
        },

        {
          path: 'taxonomy',
          name: 'taxonomy',
          component: () => import('@/views/catalog/TaxonomyView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        /*
         * The badge registry (G3 a). `catalog.products.write` rather than `.read`: the screen
         * exists only to edit the merchandising vocabulary, so there is nothing on it for an
         * account that may look at the catalogue but not change it — and the API refuses even the
         * listing to one, so a read-only operator would reach an empty screen and a 403.
         */
        /*
         * Read-only, and gated on read: the screen shows what customers asked for and offers no
         * action, so requiring write here would keep it from the buyer it is written for.
         */
        /*
         * Routines sold as one SKU (G6.7). `catalog.products.write` is what the API asks for on the
         * actions, but the screen is gated on `.read` so a buyer who may look at the catalogue can
         * see which routines exist and which component has run one out of stock — the two things
         * this screen answers that no product page can.
         */
        {
          path: 'catalog/bundles',
          name: 'catalog-bundles',
          component: () => import('@/views/catalog/BundleListView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        {
          path: 'catalog/demand',
          name: 'catalog-demand',
          component: () => import('@/views/catalog/DemandView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        {
          path: 'badges',
          name: 'badges',
          component: () => import('@/views/catalog/BadgesView.vue'),
          meta: { permission: 'catalog.products.write' },
        },
        /*
         * The shelf builder (G3 b). `catalog.products.write` for the same reason the badges screen
         * takes it: the preview endpoint — the one thing the screen is for — is write-gated server
         * side, so a read-only account would reach a form whose every keystroke answered 403.
         */
        {
          path: 'collections',
          name: 'collections',
          component: () => import('@/views/catalog/CollectionsView.vue'),
          meta: { permission: 'catalog.products.write' },
        },
        // The circles above the journey (spec 2026-10-04). Collections' permission.
        {
          path: 'stories',
          name: 'stories',
          component: () => import('@/views/merchandising/StoriesView.vue'),
          meta: { permission: 'catalog.products.write' },
        },
        /*
         * Search merchandising (G6.10). `search.manage`, the right its own API takes on every
         * endpoint — the listing included, since nothing on the screen is informational — so an
         * account without it never reaches a page that would answer 403 to its first request.
         */
        {
          path: 'search-merchandising',
          name: 'search-merchandising',
          component: () => import('@/views/catalog/SearchMerchandisingView.vue'),
          meta: { permission: 'search.manage' },
        },
        /*
         * Discount codes (§03, §06 D5). `commerce.coupons.read` rather than the write permission:
         * the usage report is a finance question — «کدها این ماه چقدر خرج داشتند» — and an account
         * that may only read it still has a screen worth opening. The editor's own calls are
         * write-gated server side and simply answer 403.
         */
        /*
         * «سئو و جست‌وجو» (G2.1, G2.3). Two screens, two permissions, listed apart because they
         * are two jobs: one edits the SEO of pages that already exist and cannot be created, the
         * other creates the only filtered listings this shop lets into an index.
         *
         * Each names the permission its own API takes — `seo.system_pages.edit` gates even the
         * registry listing, and `seo.landing_pages.manage` gates even the landing list — so an
         * account without it would land on an empty screen and a 403 rather than on a form.
         */
        {
          path: 'seo/system-pages',
          name: 'seo-system-pages',
          component: () => import('@/views/seo/SystemPageSeoView.vue'),
          meta: { permission: 'seo.system_pages.edit' },
        },
        {
          path: 'seo/landing-pages',
          name: 'seo-landing-pages',
          component: () => import('@/views/seo/LandingPagesView.vue'),
          meta: { permission: 'seo.landing_pages.manage' },
        },
        /*
         * فیدهای محصول (G6.5). `seo.system_pages.edit` because that is what the feed API takes, and
         * it takes it for a reason worth repeating here: a feed is the same kind of object as a
         * system page — a code-owned row whose settings an operator owns — so a right of its own
         * would mean a role that may edit every title on the site but not switch on the document
         * those titles are advertised in.
         */
        {
          path: 'seo/feeds',
          name: 'seo-feeds',
          component: () => import('@/views/seo/FeedsView.vue'),
          meta: { permission: 'seo.system_pages.edit' },
        },
        {
          path: 'coupons',
          name: 'coupons',
          component: () => import('@/views/commerce/CouponsView.vue'),
          meta: { permission: 'commerce.coupons.read' },
        },
        /*
         * Flash sales (G3.3). `commerce.coupons.read` for the same reason coupons use it: a
         * campaign is money off an order, the listing is the finance half of the question, and the
         * wizard's own calls are write-gated server side and simply answer 403.
         */
        {
          path: 'campaigns',
          name: 'campaigns',
          component: () => import('@/views/marketing/CampaignsView.vue'),
          meta: { permission: 'commerce.coupons.read' },
        },
        {
          path: 'admins',
          name: 'admins',
          component: () => import('@/views/settings/AdminUsersView.vue'),
          meta: { permission: 'admins.read' },
        },
        {
          path: 'activity',
          name: 'activity',
          component: () => import('@/views/settings/ActivityLogView.vue'),
          meta: { permission: 'ops.logs.read' },
        },

        {
          path: 'reviews',
          name: 'reviews',
          component: () => import('@/views/content/ReviewQueueView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        /*
         * صف بررسی (G6.3, G6.4). Two paths, one component, because the tab is a fact about the
         * page that has to survive being pasted into a colleague's message and being reached with
         * the back button — neither of which a `ref` can do. `/moderation/questions` is declared
         * after its parent, and nothing else in this router is a prefix of it, so the sidebar's
         * longest-prefix title still resolves to the right entry.
         *
         * `catalog.products.read` and not `.write`, unlike the badge and shelf screens: the API
         * gates the two queues on read and only the decisions on write, so a read-only moderator
         * has a screen worth opening — they can see what is waiting and what was decided, and the
         * buttons simply answer 403. The reviews queue at `/reviews` above has taken the same
         * right since P10 and this is the same act on the same rows.
         */
        {
          path: 'moderation',
          name: 'moderation',
          component: () => import('@/views/content/ModerationView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        {
          path: 'moderation/questions',
          name: 'moderation-questions',
          component: () => import('@/views/content/ModerationView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        {
          path: 'support',
          name: 'support',
          component: () => import('@/views/support/SupportInboxView.vue'),
          meta: { permission: 'support.read' },
        },
        {
          path: 'support/canned',
          name: 'support-canned',
          component: () => import('@/views/support/CannedRepliesView.vue'),
          meta: { permission: 'support.manage' },
        },
        {
          path: 'support/:id',
          name: 'support-thread',
          component: () => import('@/views/support/SupportInboxView.vue'),
          meta: { permission: 'support.read' },
        },
        {
          path: 'content',
          name: 'content',
          component: () => import('@/views/content/CmsView.vue'),
          meta: { permission: 'content.articles.write' },
        },
        /*
         * The section builder (G4.2). `content.builder.write` is the right that decides whether the
         * screen is worth opening: it is what every edit on it takes, and «انتشار» — which needs
         * `content.builder.publish` as well — is drawn only for an account that holds that too.
         *
         * The API is more generous than this line can be: it lets a reviewer who holds only the
         * publish right read the draft and its preview, because approving a page you were never
         * shown is not approval. `meta.permission` carries a single string, so the route names the
         * one every seeded role with either right also holds; a publish-only role, if one is ever
         * created, will need this line to become a pair.
         */
        {
          path: 'content/builder',
          name: 'builder',
          component: () => import('@/views/content/BuilderView.vue'),
          meta: { permission: 'content.builder.write' },
        },

        /*
         * A/B tests on the built pages (G6.14). `content.builder.write` for the same reason the
         * builder route above names it — it is what every mutation here takes, and «انتخاب برنده»,
         * which additionally needs `content.builder.publish`, is drawn only for an account holding
         * both. The list is declared before `:id` so `experiments` is never matched as one.
         */
        /*
         * Customer segments and broadcasts (G6.9).
         *
         * `marketing.segments.read` opens the audience screen; the editor and the delete button are
         * drawn only for `.write`, which the screen checks itself, so a read-only analyst gets a
         * working, countable view rather than a locked door.
         *
         * Both broadcast routes carry `.write` because there is no `marketing.broadcasts.read` —
         * reading and composing sit behind one right. Sending and approving are gated inside the
         * screen, so an operator who may compose but not send still gets the dry run, which is the
         * part that matters.
         */
        {
          path: 'marketing-campaigns',
          name: 'marketing-campaigns',
          component: () => import('@/views/marketing/MarketingCampaignsView.vue'),
          meta: { permission: 'marketing.campaigns.read' },
        },
        {
          path: 'segments',
          name: 'segments',
          component: () => import('@/views/marketing/SegmentsView.vue'),
          meta: { permission: 'marketing.segments.read' },
        },
        {
          path: 'broadcasts',
          name: 'broadcasts',
          component: () => import('@/views/marketing/BroadcastsView.vue'),
          meta: { permission: 'marketing.broadcasts.write' },
        },
        {
          path: 'broadcasts/:id',
          name: 'broadcast',
          component: () => import('@/views/marketing/BroadcastDetailView.vue'),
          meta: { permission: 'marketing.broadcasts.write' },
        },

        {
          path: 'experiments',
          name: 'experiments',
          component: () => import('@/views/marketing/ExperimentsView.vue'),
          meta: { permission: 'content.builder.write' },
        },
        {
          path: 'experiments/:id',
          name: 'experiment',
          component: () => import('@/views/marketing/ExperimentDetailView.vue'),
          meta: { permission: 'content.builder.write' },
        },

        /*
         * The blog (G5.5). `content/articles/new` is declared before `content/articles/:id` so the
         * literal is never matched as an id, and the two share a component: a new article is the
         * same form with nothing in it, and a second component would be the same screen written
         * twice and corrected once.
         */
        {
          path: 'content/articles',
          name: 'articles',
          component: () => import('@/views/content/ArticlesView.vue'),
          meta: { permission: 'content.articles.write' },
        },
        {
          path: 'content/articles/new',
          name: 'article-new',
          component: () => import('@/views/content/ArticleEditView.vue'),
          meta: { permission: 'content.articles.write' },
        },
        {
          path: 'content/articles/:id',
          name: 'article',
          component: () => import('@/views/content/ArticleEditView.vue'),
          meta: { permission: 'content.articles.write' },
        },
        {
          path: 'content/authors',
          name: 'authors',
          component: () => import('@/views/content/AuthorsView.vue'),
          meta: { permission: 'content.articles.write' },
        },
        {
          path: 'content/series',
          name: 'series',
          component: () => import('@/views/content/SeriesView.vue'),
          meta: { permission: 'content.articles.write' },
        },

        {
          path: 'consults',
          name: 'consults',
          component: () => import('@/views/consults/ConsultListView.vue'),
          meta: { permission: 'journeys.analytics.read' },
        },

        {
          path: 'payments',
          name: 'payments',
          component: () => import('@/views/payments/PaymentListView.vue'),
          meta: { permission: 'payments.read' },
        },

        {
          path: 'shipping',
          name: 'shipping',
          component: () => import('@/views/shipping/FulfilmentBoardView.vue'),
          meta: { permission: 'shipping.read' },
        },

        {
          path: 'products',
          name: 'products',
          component: () => import('@/views/products/ProductListView.vue'),
          meta: { permission: 'catalog.products.read' },
        },
        {
          path: 'products/:sku',
          name: 'product',
          component: () => import('@/views/products/ProductDetailView.vue'),
          meta: { permission: 'catalog.products.read' },
        },

        {
          path: 'customers',
          name: 'customers',
          component: () => import('@/views/customers/CustomerListView.vue'),
          meta: { permission: 'customers.read' },
        },
        {
          path: 'customers/:id',
          name: 'customer',
          component: () => import('@/views/customers/CustomerDetailView.vue'),
          meta: { permission: 'customers.read' },
        },

        {
          path: 'security',
          name: 'security',
          component: () => import('@/views/settings/SecurityView.vue'),
          // No permission: this is an admin's own account, not a module.
          meta: { permission: null },
        },

        {
          path: 'settings',
          name: 'settings',
          component: () => import('@/views/settings/SettingsView.vue'),
          meta: { permission: 'settings.read' },
        },
      ],
    },
    { path: '/:pathMatch(.*)*', redirect: '/dashboard' },
  ],
})

/**
 * The guard resolves the session once per page load and then trusts the store. The server is
 * still authoritative — every `/admin/v1` call is checked there — this only decides what to show.
 */
router.beforeEach(async (to) => {
  const auth = useAuthStore()

  if (!auth.hydrated) await auth.fetchMe()

  if (to.meta.public) {
    return auth.isAuthenticated ? { name: 'dashboard' } : true
  }

  if (!auth.isAuthenticated) {
    return { name: 'login', query: { next: to.fullPath } }
  }

  // Two-factor first, for the roles that require it: nothing else would load anyway.
  if (auth.mustEnrol && to.name !== 'security') {
    return { name: 'security' }
  }

  const required = to.meta.permission

  if (typeof required === 'string' && !auth.can(required)) {
    return { name: 'dashboard' }
  }

  return true
})
